import { useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { ShareCardCanvas, ShareCardData } from '@/components/share/ShareCardCanvas';
import React from 'react';

interface UseShareCardResult {
  generateShareCard: (data: ShareCardData) => Promise<Blob>;
  isGenerating: boolean;
}

export function useShareCard(): UseShareCardResult {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateShareCard = useCallback((data: ShareCardData): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      setIsGenerating(true);

      // Create a temporary container
      const container = document.createElement('div');
      container.id = 'share-card-container';
      document.body.appendChild(container);

      const root = createRoot(container);

      const handleCapture = (blob: Blob) => {
        // Cleanup
        root.unmount();
        container.remove();
        setIsGenerating(false);
        resolve(blob);
      };

      const handleError = (error: Error) => {
        // Cleanup
        root.unmount();
        container.remove();
        setIsGenerating(false);
        reject(error);
      };

      root.render(
        React.createElement(ShareCardCanvas, {
          data: data,
          onCapture: handleCapture,
          onError: handleError,
        })
      );

      // Timeout failsafe
      setTimeout(() => {
        if (container.parentNode) {
          root.unmount();
          container.remove();
          setIsGenerating(false);
          reject(new Error('Share card generation timed out'));
        }
      }, 10000);
    });
  }, []);

  return {
    generateShareCard,
    isGenerating,
  };
}
